// Client for the Insights feedback endpoint (the P1 capture UI's writer). Posts
// through the API gateway via the CloudFunctionClientContext `api` instance —
// never a direct Firebase call. Server contract:
// functions/src/insights/feedback.ts. Design: functions/docs/insights-training-
// design.md §4.

import { ApisauceInstance } from 'apisauce';

export const SUBMIT_INSIGHTS_FEEDBACK_PATH =
  '/v1/content/submitInsightsFeedback';

export type FeedbackVerdict = 'up' | 'down' | 'correction';

// Staff-only refinement (server drops it for customers); kept here so the P2
// triage inbox can reuse the type. Mirrors FEEDBACK_CATEGORIES in
// functions/src/insights/feedback.ts (source of truth) — keep in sync by hand.
export type FeedbackCategory =
  | 'wrong-metric'
  | 'wrong-dataset-field'
  | 'wrong-vis-type'
  | 'wrong-date-basis'
  | 'hallucinated-code'
  | 'missed-clarification'
  | 'factual-error'
  | 'formatting'
  | 'other'
  | 'good';

// Human-readable labels for the failure taxonomy — shared by the Feedback inbox
// picker and the Quality dashboard so a tag reads the same everywhere.
export const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  'wrong-metric': 'Wrong metric selected',
  'wrong-dataset-field': 'Wrong dataset/field',
  'wrong-vis-type': 'Wrong chart/vis type',
  'wrong-date-basis': 'Wrong date basis/period',
  'hallucinated-code': 'Made-up code/value meaning',
  'missed-clarification': 'Missed needed clarification',
  'factual-error': 'Factual/calculation error',
  formatting: 'Formatting/presentation',
  other: 'Other',
  good: 'Good',
};

// The tags a staff reviewer can assign to an incorrect answer — every category
// except the positive 'good'. Order mirrors the server taxonomy.
export const FEEDBACK_FAILURE_CATEGORIES: FeedbackCategory[] = [
  'wrong-metric',
  'wrong-dataset-field',
  'wrong-vis-type',
  'wrong-date-basis',
  'hallucinated-code',
  'missed-clarification',
  'factual-error',
  'formatting',
  'other',
];

// Unknown slugs (e.g. a server-added category the client has not mirrored yet)
// pass through unchanged rather than disappearing from a breakdown.
export const feedbackCategoryLabel = (category: string): string =>
  FEEDBACK_CATEGORY_LABELS[category as FeedbackCategory] ?? category;

export interface FeedbackExpectation {
  metricId?: string;
  dataset?: string;
  field?: string;
  answerSubstr?: string;
  knownBadSubstr?: string;
}

export interface SubmitInsightsFeedbackPayload {
  // The assistant message id (== the server query log's answerId).
  answerId?: string;
  conversationId?: string;
  verdict: FeedbackVerdict;
  category?: FeedbackCategory;
  correctionText?: string;
  expected?: FeedbackExpectation;
}

// The durable slice persisted on a chat message so a revisited conversation
// restores the user's choice (and doesn't re-invite feedback on the same answer).
export interface MessageFeedback {
  verdict: FeedbackVerdict;
}

// Fire-and-forget from the UI's perspective — a failure is logged, never
// surfaced as a blocking error (feedback must never get in the way of the chat).
export const submitInsightsFeedback = async (
  api: ApisauceInstance,
  payload: SubmitInsightsFeedbackPayload,
): Promise<boolean> => {
  const response = await api.post(SUBMIT_INSIGHTS_FEEDBACK_PATH, payload);
  return response.ok === true;
};

// Staff-only inbox actions on a feedback item (see the P2 Feedback inbox /
// functions reviewInsightsFeedback): set a terminal status once handled and/or
// (re)assign its failure category tag. At least one of status/category is
// required; a category-only call leaves the handled state untouched.
export const REVIEW_INSIGHTS_FEEDBACK_PATH =
  '/v1/content/reviewInsightsFeedback';

export const reviewInsightsFeedback = async (
  api: ApisauceInstance,
  params: {
    feedbackId: string;
    status?: 'actioned' | 'dismissed';
    category?: FeedbackCategory;
  },
): Promise<boolean> => {
  const response = await api.post(REVIEW_INSIGHTS_FEEDBACK_PATH, params);
  return response.ok === true;
};
